import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useProject } from "../../../App/state";
import { Notify } from "../../../contexts/Notifications";
import { useGetProjectConfigQuery } from "../../../generated/types";
import { Organization, Site, SiteSheet } from "./OpenSpaceSheetImportPanel";

type UseOpenSpaceSheetImportArgs = {
  loadImageFile: (file: File) => void;
  notify: Notify;
};

export const useOpenSpaceSheetImport = ({
  loadImageFile,
  notify,
}: UseOpenSpaceSheetImportArgs) => {
  const project = useProject();
  const { data: projectConfigData } = useGetProjectConfigQuery({
    variables: {
      customer: project?.customer ?? "",
      project: project?.project ?? "",
    },
    skip: !project,
    fetchPolicy: "cache-first",
  });
  const baseURL = useMemo(
    () =>
      axios.create({
        baseURL: `${process.env["OPENSPACE_MAPPING_SERVICE"]}`,
        timeout: 1500000,
        withCredentials: true,
      }),
    [],
  );

  const [selectedRegion, setSelectedRegion] = useState<string>();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<string>();
  const [sites, setSites] = useState<Site[]>([]);
  const [selectedSite, setSelectedSite] = useState<string>();
  const [siteSheets, setSiteSheets] = useState<SiteSheet[]>([]);
  const [selectedSiteSheet, setSelectedSiteSheet] = useState<string>();
  const [openSpaceError, setOpenSpaceError] = useState<string | null>(null);
  const [openSpaceLoading, setOpenSpaceLoading] = useState(false);
  const [orgLoading, setOrgLoading] = useState(false);
  const [siteLoading, setSiteLoading] = useState(false);
  const [sheetLoading, setSheetLoading] = useState(false);
  const [prefilledOrg, setPrefilledOrg] = useState(false);
  const [prefilledSite, setPrefilledSite] = useState(false);

  const storedProjectConfig = projectConfigData?.projects?.[0]?.config;

  const handleRegionChange = useCallback((value: string) => {
    setSelectedRegion(value);
    setSelectedOrganization(undefined);
    setSelectedSite(undefined);
    setSelectedSiteSheet(undefined);
    setOrganizations([]);
    setSites([]);
    setSiteSheets([]);
    setOpenSpaceError(null);
    setPrefilledOrg(false);
    setPrefilledSite(false);
  }, []);

  useEffect(() => {
    const fetchOrganizations = async () => {
      if (!selectedRegion || !project) {
        setOrganizations([]);
        return;
      }
      try {
        setOpenSpaceError(null);
        setOrgLoading(true);
        const response = await baseURL.get(`/get-organization`, {
          params: {
            region: selectedRegion,
            customer: project.customer,
            project: project.project,
          },
        });
        setOrganizations(response.data.content);
      } catch (error) {
        console.error("Error fetching organizations:", error);
        if (axios.isAxiosError(error)) {
          setOpenSpaceError(
            `Error fetching organizations: ${
              error.response?.data?.detail || error.message
            }`,
          );
        } else {
          setOpenSpaceError(
            "An unexpected error occurred while fetching organizations.",
          );
        }
      } finally {
        setOrgLoading(false);
      }
    };

    fetchOrganizations();
  }, [baseURL, project, selectedRegion]);

  const handleOrganizationChange = useCallback(
    async (value: string) => {
      if (!project) {
        return;
      }
      setSelectedOrganization(value);
      setSelectedSite(undefined);
      setSelectedSiteSheet(undefined);
      setSites([]);
      setSiteSheets([]);
      setPrefilledSite(false);
      try {
        setOpenSpaceError(null);
        setSiteLoading(true);
        const response = await baseURL.get(`/get-sites/${value}`, {
          params: {
            region: selectedRegion,
            customer: project.customer,
            project: project.project,
          },
        });
        setSites(response.data.content);
      } catch (error) {
        console.error("Error fetching sites:", error);
        if (axios.isAxiosError(error)) {
          setOpenSpaceError(
            `Error fetching sites: ${
              error.response?.data?.detail || error.message
            }`,
          );
        } else {
          setOpenSpaceError(
            "An unexpected error occurred while fetching sites.",
          );
        }
      } finally {
        setSiteLoading(false);
      }
    },
    [baseURL, project, selectedRegion],
  );

  const handleSiteChange = useCallback(
    async (value: string) => {
      if (!project) {
        return;
      }
      setSelectedSite(value);
      setSelectedSiteSheet(undefined);
      setSiteSheets([]);
      try {
        setOpenSpaceError(null);
        setSheetLoading(true);
        const response = await baseURL.get(`/get-site-sheets/${value}`, {
          params: {
            region: selectedRegion,
            customer: project.customer,
            project: project.project,
          },
        });
        setSiteSheets(response.data.content);
      } catch (error) {
        console.error("Error fetching site sheets:", error);
        if (axios.isAxiosError(error)) {
          setOpenSpaceError(
            `Error fetching site sheets: ${
              error.response?.data?.detail || error.message
            }`,
          );
        } else {
          setOpenSpaceError(
            "An unexpected error occurred while fetching site sheets.",
          );
        }
      } finally {
        setSheetLoading(false);
      }
    },
    [baseURL, project, selectedRegion],
  );

  const handleSheetChange = useCallback((value: string) => {
    setSelectedSiteSheet(value);
  }, []);

  useEffect(() => {
    if (!storedProjectConfig) {
      return;
    }
    if (!selectedRegion && storedProjectConfig.region) {
      setSelectedRegion(storedProjectConfig.region);
    }
  }, [selectedRegion, storedProjectConfig]);

  useEffect(() => {
    if (
      !storedProjectConfig ||
      prefilledOrg ||
      !storedProjectConfig.organization ||
      !storedProjectConfig.region ||
      selectedOrganization
    ) {
      return;
    }
    if (selectedRegion === storedProjectConfig.region) {
      handleOrganizationChange(storedProjectConfig.organization);
      setPrefilledOrg(true);
    }
  }, [
    handleOrganizationChange,
    prefilledOrg,
    selectedRegion,
    selectedOrganization,
    storedProjectConfig,
  ]);

  useEffect(() => {
    if (
      !storedProjectConfig ||
      prefilledSite ||
      !storedProjectConfig.sites?.length ||
      !storedProjectConfig.organization ||
      selectedSite
    ) {
      return;
    }
    if (selectedOrganization === storedProjectConfig.organization) {
      handleSiteChange(storedProjectConfig.sites[0]);
      setPrefilledSite(true);
    }
  }, [
    handleSiteChange,
    prefilledSite,
    selectedSite,
    selectedOrganization,
    storedProjectConfig,
  ]);

  const handleOpenSpaceImport = useCallback(async () => {
    if (!project) {
      return;
    }
    if (!selectedRegion || !selectedSite || !selectedSiteSheet) {
      notify("Please select region, site, and sheet first.", "warning");
      return;
    }

    try {
      setOpenSpaceError(null);
      setOpenSpaceLoading(true);
      const response = await baseURL.get(
        `/floorplan/${selectedSite}/${selectedSiteSheet}`,
        {
          params: {
            region: selectedRegion,
            customer: project.customer,
            project: project.project,
          },
          responseType: "blob",
        },
      );
      const selectedSheet = siteSheets.find(
        (sheet) => sheet.id === selectedSiteSheet,
      );
      const filename = selectedSheet
        ? `${selectedSheet.name}.png`
        : `openspace-${selectedSiteSheet}.png`;
      const file = new File([response.data], filename, { type: "image/png" });
      loadImageFile(file);
    } catch (error) {
      console.error("Error downloading floorplan:", error);
      if (axios.isAxiosError(error)) {
        setOpenSpaceError(
          `Error downloading floorplan: ${
            error.response?.data?.detail || error.message
          }`,
        );
      } else {
        setOpenSpaceError(
          "An unexpected error occurred while downloading floorplan.",
        );
      }
    } finally {
      setOpenSpaceLoading(false);
    }
  }, [
    baseURL,
    loadImageFile,
    notify,
    project,
    selectedRegion,
    selectedSite,
    selectedSiteSheet,
    siteSheets,
  ]);

  return {
    selectedRegion,
    selectedOrganization,
    selectedSite,
    selectedSiteSheet,
    organizations,
    sites,
    siteSheets,
    openSpaceError,
    openSpaceLoading,
    orgLoading,
    siteLoading,
    sheetLoading,
    handleRegionChange,
    handleOrganizationChange,
    handleSiteChange,
    handleSheetChange,
    handleOpenSpaceImport,
  };
};
